/**
 * Health calculators — pure formulas + unit conversions.
 *
 * Everything is computed in metric internally (kg, cm); the UI converts
 * imperial input first. All functions return `null` on invalid input.
 * Informational only — not medical advice.
 */

function finite(...nums: number[]): boolean {
  return nums.every((n) => typeof n === "number" && Number.isFinite(n));
}

export function lbToKg(lb: number): number {
  return lb * 0.45359237;
}
export function ftInToCm(feet: number, inches: number): number {
  return (feet * 12 + inches) * 2.54;
}

/** Body Mass Index = kg / m². */
export function bmi(weightKg: number, heightCm: number): number | null {
  if (!finite(weightKg, heightCm) || heightCm <= 0 || weightKg <= 0) {
    return null;
  }
  const m = heightCm / 100;
  return weightKg / (m * m);
}

export type BmiCategory = "underweight" | "normal" | "overweight" | "obese";

export function bmiCategory(value: number): BmiCategory {
  if (value < 18.5) return "underweight";
  if (value < 25) return "normal";
  if (value < 30) return "overweight";
  return "obese";
}

export type Sex = "male" | "female";

/** Basal Metabolic Rate (Mifflin-St Jeor), kcal/day. */
export function bmr(
  weightKg: number,
  heightCm: number,
  age: number,
  sex: Sex,
): number | null {
  if (
    !finite(weightKg, heightCm, age) ||
    weightKg <= 0 ||
    heightCm <= 0 ||
    age <= 0
  ) {
    return null;
  }
  const s = sex === "male" ? 5 : -161;
  return 10 * weightKg + 6.25 * heightCm - 5 * age + s;
}

export const ACTIVITY_FACTORS: Record<string, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  veryActive: 1.9,
};

export function activityFactor(level: string): number | undefined {
  return Object.hasOwn(ACTIVITY_FACTORS, level)
    ? ACTIVITY_FACTORS[level]
    : undefined;
}

/** Total Daily Energy Expenditure = BMR × activity factor. */
export function tdee(bmrValue: number, factor: number): number | null {
  if (!finite(bmrValue, factor) || bmrValue <= 0 || factor <= 0) return null;
  return bmrValue * factor;
}

/** Healthy weight range (kg) for a height, using the BMI 18.5–24.9 band. */
export function healthyWeightRange(
  heightCm: number,
): { min: number; max: number } | null {
  if (!finite(heightCm) || heightCm <= 0) return null;
  const m = heightCm / 100;
  return { min: 18.5 * m * m, max: 24.9 * m * m };
}
